import React, { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import {
  DataTable,
  DisplayOrder,
  InstrumentFilter,
  SubHeading,
} from "../Components";
import {
  deleteAxios,
  getAxios,
  patchAxios,
  postAxios,
} from "../Services/commonService";
import {
  API_INSTRUMENT_DELETE,
  API_INSTRUMENT_LIST,
  API_INSTRUMENT_UPDATE,
} from "../utils/api/ApiConstant";
import { ConfimationModal } from "../Modal";
import Icon from "../assets/icons/Icon";

const Instrument = () => {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [exchange, setExchange] = useState("all");
  const [isShow, setIsShow] = useState({
    isOpen: false,
    rowData: null,
    name: "",
    modalTitle: "",
    modalContent: null,
    apiFlag: false,
  });

  const {
    isLoading,
    data: instrumentData,
    refetch,
  } = useQuery({
    queryKey: [
      "instrumentData",
      currentPage,
      recordsPerPage,
      searchTerm,
      exchange,
      isShow.apiFlag,
    ],
    queryFn: async () =>
      await getAxios({
        url: API_INSTRUMENT_LIST,
        params: {
          page: currentPage,
          limit: recordsPerPage,
          search: searchTerm,
          exchange,
        },
      }),
  });

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleRecordsperPage = useCallback((value) => {
    setRecordsPerPage(value);
  }, []);

  const handleSearch = useCallback((value) => {
    setSearchTerm(value);
  }, []);

  const columns = useMemo(
    () => [
      {
        accessorKey: "exchange",
        header: "Exchange",
      },
      {
        accessorKey: "name",
        header: "name",
      },
      {
        accessorKey: "expiry",
        header: "Expiry	",
      },
      {
        accessorKey: "lot_size",
        header: "LotSize",
        cell: ({ row }) => {
          return (
            <div className="action_icon_box">
              <DisplayOrder
                initialValue={row?.original?.lot_size}
                onConfirm={(newValue) =>
                  handlePutRequest({
                    url: API_INSTRUMENT_UPDATE,
                    rowData: row?.original,
                    newValue,
                    key: "lot_size",
                  })
                }
              />
            </div>
          );
        },
      },
      {
        accessorKey: "exchange_token",
        header: "Exch Token",
      },
      {
        accessorKey: "instrument_token",
        header: "Inst. Token",
      },
      {
        accessorKey: "isDefault",
        header: "Is Default",
        cell: ({ row }) => (
          <ConfimationModal
            isSwitch={true}
            getValue={row?.original?.isDefault}
            onStatusChange={(newValue) =>
              handlePutRequest({
                url: API_INSTRUMENT_UPDATE,
                rowData: row?.original,
                newValue,
                key: "isDefault",
              })
            }
          />
        ),
      },
      {
        accessorKey: "tradingsymbol",
        header: "Trending Symbols",
      },
      {
        accessorKey: "displayOrder",
        header: "Display Order",
        cell: ({ row }) => {
          return (
            <div className="action_icon_box">
              <DisplayOrder
                initialValue={row?.original?.displayOrder}
                onConfirm={(newValue) =>
                  handlePutRequest({
                    url: API_INSTRUMENT_UPDATE,
                    rowData: row?.original,
                    newValue,
                    key: "displayOrder",
                  })
                }
              />
            </div>
          );
        },
      },
      {
        accessorKey: "isBadla",
        header: "Is Badla",
        cell: ({ row }) => (
          <ConfimationModal
            isSwitch={true}
            getValue={row?.original?.isBadla}
            onStatusChange={(newValue) =>
              handlePutRequest({
                url: API_INSTRUMENT_UPDATE,
                rowData: row?.original,
                newValue,
                key: "isBadla",
              })
            }
          />
        ),
      },
      {
        accessorKey: "strike",
        header: "Strike",
      },
      {
        accessorKey: "instrument_type",
        header: "Instrument Type",
      },
      {
        accessorKey: "serverSync",
        header: "SyncIDs",
      },
      {
        accessorKey: "via",
        header: "Source",
      },
      {
        accessorKey: "action",
        header: "actions",
        cell: ({ row }) => {
          return (
            <>
              <Icon
                name="MdDelete"
                className="m-1"
                title="Delete"
                onClick={() => handleDeleteRequest({ rowData: row?.original })}
              />
            </>
          );
        },
      },
    ],
    []
  );

  const fetchData = useCallback(() => {
    if (!instrumentData) return { data: [], pages: 0 };

    return {
      data: instrumentData.docs,
      pages: instrumentData.totalPages,
    };
  }, [instrumentData]);

  const handleQueryClient = (rowData, updatedRowData, key) => {
    return queryClient.setQueryData(
      [
        "instrumentData",
        currentPage,
        recordsPerPage,
        searchTerm,
        exchange,
        isShow.apiFlag,
      ],
      (oldData) => {
        return {
          ...oldData,
          docs: oldData?.docs.map((obj) =>
            obj._id === rowData._id
              ? { ...obj, [key]: updatedRowData[key] }
              : obj
          ),
        };
      }
    );
  };

  const handlePutRequest = async ({ url, rowData, newValue, key }) => {
    const payload = { ...rowData, [key]: newValue };
    const customPayload = {
      _id: payload?._id,
      displayOrder: payload?.displayOrder,
      isBadla: payload?.isBadla,
      isDefault: payload?.isDefault,
      lot_size: payload?.lot_size,
    };

    const res = await patchAxios({ url, payload: customPayload });

    if (res) {
      refetch();
    }
  };

  const handleDeleteRequest = async ({ rowData }) => {
    const payload =
      Object.keys(rowData).length !== 0
        ? { _id: rowData?._id }
        : { _id: selectedIds };

    const res = await deleteAxios({
      url: API_INSTRUMENT_DELETE,
      payload,
    });

    if (res) {
      setIsShow((prev) => ({
        ...prev,
        apiFlag: !prev.apiFlag,
      }));
    }
  };

  const handleSelectionChange = useCallback((selected) => {
    setSelectedIds(selected); // Selected IDs will be passed here
  }, []);

  const handleExchangeChange = useCallback(
    (exchangeId) => setExchange(exchangeId || "all"),
    []
  );

  return (
    <>
      <SubHeading subTitle="Instrument" isAddBtn={false} />
      <InstrumentFilter
        handleExchangeChange={handleExchangeChange}
        handleDeleteRequest={handleDeleteRequest}
      />
      <div className="card-body">
        <DataTable
          columns={columns}
          fetchData={fetchData}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onRecordsPerPageChange={handleRecordsperPage}
          recordsPerPage={recordsPerPage}
          onSearchChange={handleSearch}
          isLoading={isLoading}
          isIndexing={false}
          isCheckBox={true}
          onSelectionChange={handleSelectionChange}
          handleSearchKey={setIsShow}
        />
      </div>
    </>
  );
};

export default Instrument;
